import { useEffect, useMemo, useRef } from 'react';

import { di } from 'react-magnetic-di';

import { type ExperienceNameType } from '../../controllers/experience-tracker/constants';
import { useAIMateExperienceTracker } from '../../controllers/experience-tracker/main';

type ExperienceProps = {
	name: ExperienceNameType;
};

type ExperienceSuccessProps = ExperienceProps & {
	/**
	 * Component lifecycle event that should trigger experience success
	 * @default "mount"
	 */
	trigger?: 'mount' | 'unmount';
	attributes?: Record<string, unknown>;
};

export const ExperienceSuccess = ({
	name,
	trigger = 'mount',
	attributes,
}: ExperienceSuccessProps) => {
	di(useAIMateExperienceTracker);
	const successTriggerRef = useRef(trigger);
	const attributesRef = useRef(attributes);

	const experienceTracker = useAIMateExperienceTracker();

	useEffect(() => {
		const successAttributes = attributesRef.current;
		if (successTriggerRef.current === 'mount') {
			experienceTracker.succeed({
				name,
				...(successAttributes && { attributes: successAttributes }),
			});
		}
		if (successTriggerRef.current === 'unmount') {
			return () => {
				experienceTracker.succeed({
					name,
					...(successAttributes && { attributes: successAttributes }),
				});
			};
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return null;
};

type ExperienceFailureProps = ExperienceProps & {
	error: Error;
	teamName?: string;
};

export const ExperienceFailure = ({ error, name, teamName }: ExperienceFailureProps) => {
	di(useAIMateExperienceTracker);

	const experienceTracker = useAIMateExperienceTracker();

	useEffect(() => {
		experienceTracker.fail({
			name,
			error,
			teamName,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return null;
};

type ExperienceStartProps = ExperienceProps & {
	attributes?: Record<string, unknown>;
};

export const ExperienceStart = ({ name, attributes }: ExperienceStartProps) => {
	di(useAIMateExperienceTracker);

	const experienceTracker = useAIMateExperienceTracker();

	useMemo(() => {
		experienceTracker.start({
			id: name,
			name,
			attributes,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return null;
};
